import { Link } from 'react-router-dom';
import { TEMPLATES } from '@/types';
import { Check, Crown } from 'lucide-react';

function TemplateThumb({ name }: { name: string }) {
  return (
    <div className="flex h-40 w-full items-center justify-center overflow-hidden rounded-xl border border-dashed border-[#E2E8F0] bg-[#F8FAFC]">
      <div className="w-full max-w-[180px] space-y-2 p-3">
        <div className="h-2 w-2/3 rounded bg-[#0A2540]" />
        <div className="space-y-1.5">
          <div className="h-1 w-full rounded bg-[#CBD5E0]" />
          <div className="h-1 w-5/6 rounded bg-[#CBD5E0]" />
          <div className="h-1 w-4/5 rounded bg-[#CBD5E0]" />
        </div>
        <div className="h-1 w-2/3 rounded bg-[#CBD5E0]" />
        <div className="space-y-1">
          <div className="h-0.5 w-full rounded bg-[#E2E8F0]" />
          <div className="h-0.5 w-11/12 rounded bg-[#E2E8F0]" />
          <div className="h-0.5 w-5/6 rounded bg-[#E2E8F0]" />
        </div>
      </div>
      <span className="sr-only">{name}</span>
    </div>
  );
}

interface TemplateGalleryProps {
  linkTo?: string;
  featuredId?: string;
  showUseButton?: boolean;
  onSelect?: (id: string) => void;
  darkMode?: boolean;
}

export function TemplateGallery({
  linkTo = '/app/create',
  featuredId = 'modern-clean',
  showUseButton = true,
  onSelect,
  darkMode = false,
}: TemplateGalleryProps) {
  const cardBody = (template: (typeof TEMPLATES)[number]) => {
    const isFeatured = template.id === featuredId;
    const featuredClasses = isFeatured ? 'lg:scale-105' : '';
    const hoverGlow = darkMode
      ? 'hover:-translate-y-2 hover:border-[rgba(0,212,170,0.35)] hover:shadow-[0_24px_48px_-12px_rgba(0,0,0,0.35),0_0_30px_rgba(0,212,170,0.15)]'
      : 'hover:-translate-y-2 hover:border-[rgba(0,212,170,0.35)] hover:shadow-[0_24px_48px_-12px_rgba(0,0,0,0.35),0_0_30px_rgba(0,212,170,0.15)]';

    return (
      <div
        className={`group relative rounded-[24px] border border-[#E2E8F0] bg-white p-5 shadow-lg transition-all duration-300 ${hoverGlow} ${featuredClasses}`}
      >
        {isFeatured && (
          <div className="absolute -top-3 left-5 z-10 rounded-md bg-gradient-to-r from-[#635BFF] to-[#00D4AA] px-3 py-1 text-xs font-extrabold text-white">
            Most Popular
          </div>
        )}
        {template.isPro && (
          <div className="absolute bottom-5 right-5 z-10 inline-flex items-center gap-1 rounded bg-[#635BFF] px-2 py-0.5 text-xs font-semibold text-white">
            <Crown size={10} /> Pro
          </div>
        )}
        <div className="overflow-hidden rounded-xl">
          <div className="template-thumb-zoom">
            <TemplateThumb name={template.name} />
          </div>
        </div>
        <div className="mt-4">
          <div className="font-semibold text-[#0A2540]">{template.name}</div>
          <div className="text-sm text-[#718096]">{template.description}</div>
        </div>
        {showUseButton && (
          <div className="mt-4 flex items-center gap-2 text-sm font-semibold text-[#00D4AA]">
            <Check size={16} />
            Use This Template
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Desktop grid — no 3D rotation */}
      <div className="hidden grid-cols-1 gap-6 sm:grid sm:grid-cols-2 lg:grid-cols-3">
        {TEMPLATES.map((template) => {
          if (onSelect) {
            return (
              <button
                key={template.id}
                type="button"
                onClick={() => onSelect(template.id)}
                className="group w-full text-left"
              >
                {cardBody(template)}
              </button>
            );
          }
          return (
            <Link
              key={template.id}
              to={`${linkTo}?template=${template.id}`}
              className="group block"
            >
              {cardBody(template)}
            </Link>
          );
        })}
      </div>

      {/* Mobile single-column layout */}
      <div className="grid grid-cols-1 gap-5 sm:hidden">
        {TEMPLATES.map((template) => {
          const cardBody = (
            <div className="template-card-v5 relative rounded-[24px] border border-[#E2E8F0] bg-white p-5 shadow-lg">
              {template.id === featuredId && (
                <div className="absolute -top-3 left-5 z-10 rounded-md bg-gradient-to-r from-[#635BFF] to-[#00D4AA] px-3 py-1 text-xs font-extrabold text-white">
                  Most Popular
                </div>
              )}
              {template.isPro && (
                <div className="absolute bottom-5 right-5 z-10 inline-flex items-center gap-1 rounded bg-[#635BFF] px-2 py-0.5 text-xs font-semibold text-white">
                  <Crown size={10} /> Pro
                </div>
              )}
              <TemplateThumb name={template.name} />
              <div className="mt-4">
                <div className="font-semibold text-[#0A2540]">{template.name}</div>
                <div className="text-sm text-[#718096]">{template.description}</div>
              </div>
              {showUseButton && (
                <div className="mt-4 flex items-center gap-2 text-sm font-semibold text-[#00D4AA]">
                  <Check size={16} />
                  Use This Template
                </div>
              )}
            </div>
          );
          if (template.isPro) {
            return <div key={template.id}>{cardBody}</div>;
          }
          return onSelect ? (
            <button
              key={template.id}
              type="button"
              onClick={() => onSelect(template.id)}
              className="group w-full text-left"
            >
              {cardBody}
            </button>
          ) : (
            <Link
              key={template.id}
              to={`${linkTo}?template=${template.id}`}
              className="group block"
            >
              {cardBody}
            </Link>
          );
        })}
      </div>
    </>
  );
}

export default TemplateGallery;
